import {
  ACCESS_KEY_ALPHABET,
  ACCESS_KEY_LENGTH,
  REFERRAL_CODE_ALPHABET,
  REFERRAL_CODE_LENGTH,
  normaliseAccessKeyText,
  withoutCheckCharacter,
} from './code-check-character';
import { CODE_LENGTHS_RESERVED_FOR_OTHER_CODES } from './promo-campaign';
import { isPlausibleCode } from './referral';

// The Subscribe page's one code field (#6984) tells the kinds of code apart by
// SHAPE, before any call is made, so a friend's code or an access code is never
// sent to the public sale lookup, and a near-miss is answered on the device.
// The rules and the reasons: design-docs/subscribe-code-field.md, "The classifier".

/** What a typed code looks like. Decided from its shape alone; nothing is looked up. */
export enum TypedCodeShape {
  /** 8 characters of the referral alphabet, or 9 with a correct check character */
  Friend = 'Friend',
  /** 16 characters of the access-key alphabet, or 17 with a correct check character */
  Access = 'Access',
  /** 9 or 17 characters in the matching alphabet whose check character is wrong: a typo */
  Mistyped = 'Mistyped',
  /** 7 characters of the referral alphabet, or 15 of the access-key alphabet: a character short */
  Incomplete = 'Incomplete',
  /** any other code of a length no sale code may have (7, 8, 9, 15, 16, 17) */
  NotRecognised = 'NotRecognised',
  /** everything else: a sale code, for the sale lookup */
  Other = 'Other',
}

export interface TypedCode {
  shape: TypedCodeShape;
  /** For Friend and Access only: the code without its check character, upper-case (8 or 16 characters).
   *  An access key is still normalised again by the server, which stores it in lower case. */
  storedCode?: string;
}

/**
 * A typed code with everything that is not a letter or a digit removed (spaces,
 * dashes, the en-dash and em-dash a phone keyboard inserts) and upper-cased.
 * Nothing real is lost: every kind of code is letters and digits only.
 */
export function normaliseTypedCode(raw: string | undefined | null): string {
  return (raw ?? '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
}

function isAllIn(text: string, alphabet: string): boolean {
  for (const ch of text) {
    if (!alphabet.includes(ch)) return false;
  }
  return true;
}

/** Sorts a 7-, 8- or 9-character code: a friend's code, a typo, a short one, or nothing we issue. */
function classifyFriendLength(code: string): TypedCode {
  if (!isAllIn(code, REFERRAL_CODE_ALPHABET)) return { shape: TypedCodeShape.NotRecognised };
  if (code.length < REFERRAL_CODE_LENGTH) return { shape: TypedCodeShape.Incomplete };
  const body = code.slice(0, REFERRAL_CODE_LENGTH);
  if (!isPlausibleCode(body)) return { shape: TypedCodeShape.NotRecognised };
  if (code.length === REFERRAL_CODE_LENGTH) return { shape: TypedCodeShape.Friend, storedCode: body };
  return withoutCheckCharacter(code, REFERRAL_CODE_ALPHABET, REFERRAL_CODE_LENGTH)
    ? { shape: TypedCodeShape.Friend, storedCode: body }
    : { shape: TypedCodeShape.Mistyped };
}

/** Sorts a 15-, 16- or 17-character code: an access code, a typo, a short one, or nothing we issue. */
function classifyAccessLength(code: string): TypedCode {
  const key = normaliseAccessKeyText(code);
  if (!isAllIn(key, ACCESS_KEY_ALPHABET)) return { shape: TypedCodeShape.NotRecognised };
  if (key.length < ACCESS_KEY_LENGTH) return { shape: TypedCodeShape.Incomplete };
  const body = key.slice(0, ACCESS_KEY_LENGTH);
  if (key.length === ACCESS_KEY_LENGTH) return { shape: TypedCodeShape.Access, storedCode: body };
  return withoutCheckCharacter(key, ACCESS_KEY_ALPHABET, ACCESS_KEY_LENGTH)
    ? { shape: TypedCodeShape.Access, storedCode: body }
    : { shape: TypedCodeShape.Mistyped };
}

/** What a typed code is, from its shape. See TypedCodeShape. */
export function classifyTypedCode(raw: string | undefined | null): TypedCode {
  const code = normaliseTypedCode(raw);
  // the friend lengths (7-9) and the access lengths (15-17) do not overlap, so no code is both
  if (code.length >= REFERRAL_CODE_LENGTH - 1 && code.length <= REFERRAL_CODE_LENGTH + 1) return classifyFriendLength(code);
  if (code.length >= ACCESS_KEY_LENGTH - 1 && code.length <= ACCESS_KEY_LENGTH + 1) return classifyAccessLength(code);
  // a guard against the two lists drifting apart: every length above must be one no sale code may have
  if (CODE_LENGTHS_RESERVED_FOR_OTHER_CODES.includes(code.length)) return { shape: TypedCodeShape.NotRecognised };
  return { shape: TypedCodeShape.Other };
}
